/**
 * Which of your goals today's plan actually moves.
 *
 * Goals lived entirely on their own screen. Every task carries `goalIds`, the API has always
 * sent them, and no list ever displayed one — so the day view could not tell you that four
 * hours of planned work served nothing you had said you were trying to do, and the goals screen
 * could not tell you that its 40% had no time booked against it this week.
 *
 * That disconnection is the whole problem with long-term goals in practice. Nobody abandons a
 * goal deliberately; days just fill with work that belongs to something else, and there is no
 * moment where the two views are put side by side. This is that moment.
 *
 * Pure and separate from the component so the pairing can be tested as data — the interesting
 * cases are all about tasks with several goals, or none.
 */

export interface FocusTask {
  id: string;
  title: string;
  goalIds: string[];
}

export interface FocusGoal {
  id: string;
  title: string;
  /** From the pace verdict. Behind goals are surfaced first. */
  paceState: string;
  needsNextAction: boolean;
  /** Why nothing is open: finished and never-started need opposite sentences. */
  nextActionState: 'none' | 'not_started' | 'ready_to_finish';
  /**
   * Whether the goal is still being pursued, off the payload — `isLive` in core, `live` on the
   * wire. A pause, or a finish, is a decision, not a thing to nag about.
   */
  live: boolean;
  percent: number;
}

interface GoalFocus {
  goal: FocusGoal;
  /** Tasks with a block on the day being viewed. */
  scheduled: FocusTask[];
  /** Tasks on the shortlist for this goal, so an unserved goal has an obvious remedy. */
  available: FocusTask[];
}

export interface FocusResult {
  /** One entry per active goal, most urgent first. */
  goals: GoalFocus[];
  /** Planned tasks attached to no goal at all. */
  unaligned: FocusTask[];
  /**
   * Active goals with nothing on the day **and** nothing on their shortlist.
   *
   * It used to be `scheduled.length === 0` alone, which is *not on the timeline* wearing the word
   * "untouched" — the same one-word-two-predicates reading that put "nothing planned" above six
   * goals' own shortlists in the 1.39.0 persona round. A goal whose work is sitting on the
   * shortlist has been touched; it has not been dated.
   *
   * **Nothing in the app reads this today** — `GoalsToday` renders each row from its own `scheduled`
   * and `available`, and the ranking below reads `scheduled` directly. So this change moves no pixel.
   * It is here because a field whose name and docstring disagree with the screen beside it is the
   * next reader's trap, and the screen has just been corrected.
   */
  untouchedCount: number;
}

/**
 * Ranking: what deserves attention soonest.
 *
 * A goal that is behind *and* has nothing planned today is the worst case and sorts first. A
 * goal already being worked on today sorts last regardless of pace — it does not need a nudge,
 * because the nudge already worked.
 */
const URGENCY: Record<string, number> = {
  overdue: 0,
  behind: 1,
  on_track: 2,
  no_target: 3,
  ahead: 4,
  done: 5,
};

function rank(entry: GoalFocus): number {
  const served = entry.scheduled.length > 0 ? 100 : 0;
  const urgency = URGENCY[entry.goal.paceState] ?? 3;
  // Needing a next action is a stronger signal than pace, so it shifts a whole band.
  const stalled = entry.goal.needsNextAction ? -0.5 : 0;
  return served + urgency + stalled;
}

export function computeGoalFocus(input: {
  goals: readonly FocusGoal[];
  /** Tasks with a block on this day. */
  scheduledTasks: readonly FocusTask[];
  /** Tasks offered for this day but not yet placed. */
  candidates: readonly FocusTask[];
}): FocusResult {
  const entries: GoalFocus[] = input.goals.map((goal) => ({
    goal,
    scheduled: input.scheduledTasks.filter((t) => t.goalIds.includes(goal.id)),
    available: input.candidates.filter((t) => t.goalIds.includes(goal.id)),
  }));

  entries.sort((a, b) => rank(a) - rank(b) || a.goal.title.localeCompare(b.goal.title));

  return {
    goals: entries,
    // A task can serve several goals or none. "None" is not a fault — capture is meant to be
    // frictionless — but a whole day of it is worth seeing.
    unaligned: input.scheduledTasks.filter((t) => t.goalIds.length === 0),
    untouchedCount: entries.filter((e) => e.scheduled.length === 0 && e.available.length === 0)
      .length,
  };
}

/**
 * Goal titles for a task, in the order the goals were given.
 *
 * Returns titles rather than ids because a chip showing `01a00c03…` is worse than no chip, and
 * a task may reference a goal that has since been deleted — in which case it is dropped rather
 * than rendered as a blank.
 */
export function goalTitlesFor(
  task: { goalIds: string[] },
  titles: ReadonlyMap<string, string>,
): string[] {
  return task.goalIds.map((id) => titles.get(id)).filter((t): t is string => t !== undefined);
}

/**
 * The chips a task should show: what it serves, and in which colour.
 *
 * Colour is a second channel on a label that already exists — the chip has printed the goal's
 * name since Phase 17 — so it is never the only signal.
 */
export function goalChipsFor(
  task: { goalIds: string[] },
  chips: ReadonlyMap<string, { title: string; colorClass: string }>,
): { title: string; colorClass: string }[] {
  return task.goalIds
    .map((id) => chips.get(id))
    .filter((c): c is { title: string; colorClass: string } => c !== undefined);
}
